<template>
	<n-scrollbar class="notifications-list">
		<div
			v-for="item of displayItems"
			:key="item.id"
			class="item flex cursor-pointer"
			:class="[
				{ pointer: !!item.action || item.isChangelog },
				item.type,
				item.read ? 'bg-gray-200' : '',
				{ 'item--changelog': item.isChangelog },
			]"
			@click="onItemClick(item)"
		>
			<div class="icon-box" @click.stop="setRead(item.id)">
				<Icon :name="MessageIcon" :size="21" v-if="item.category === 'message'" />
				<Icon :name="CalendarIcon" :size="21" v-else-if="item.category === 'reminder'" />
				<Icon :name="AddIcon" :size="21" v-else-if="item.category === 'new'" />
				<Icon :name="AlertIcon" :size="21" v-else-if="item.category === 'alert'" />
				<Icon :name="ChangelogIcon" :size="21" v-else-if="item.category === 'changelog'" />
				<n-tooltip trigger="hover" style="padding: 0" placement="right" v-if="!item.read">
					<template #trigger>
						<div class="read-badge" />
					</template>
				</n-tooltip>
			</div>
			<div class="content grow min-w-0">
				<div v-if="item.isChangelog" class="title flex flex-wrap items-center gap-2">
					<span class="truncate">{{ item.title }}</span>
					<span v-if="item.version" class="version-tag shrink-0 rounded bg-primary/15 px-1.5 py-0.5 text-xs font-medium text-primary">
						v{{ item.version }}
					</span>
				</div>
				<div v-else class="title" v-html="item.title" />
				<div
					v-if="item.description"
					class="description"
					:class="{ 'description--plain': item.isChangelog }"
				>
					<template v-if="item.isChangelog">{{ item.description }}</template>
					<span v-else v-html="item.description" />
				</div>
				<div class="footer flex items-center justify-between">
					<div class="date">{{ item.date }}</div>
					<div v-if="item.isChangelog" class="action-text">{{ t("changelog.view_all") }}</div>
					<div v-else-if="item.action" class="action-text">{{ item.actionTitle || "Details" }}</div>
				</div>
			</div>
		</div>
		<slot name="last" />
		<n-empty
			v-if="!displayItems.length"
			:description="t('notifications.empty')"
			class="h-48 justify-center"
		/>
	</n-scrollbar>
</template>

<script lang="ts" setup>
import { NScrollbar, NTooltip, NEmpty } from "naive-ui"
import Icon from "@/components/common/Icon.vue"
import { useNotifications, type Notification } from "@/composables/useNotifications"
import { computed } from "vue"
import { useRouter } from "vue-router"
import _take from "lodash/take"
import { useNotificationStore } from "@/stores/apps/notification"
import { useChangelogStore } from "@/stores/apps/changelog"
import { useStoreI18n } from "@/composables/useStoreI18n"
import { axiosToken } from "@/utils/http"

const MessageIcon = "carbon:email"
const CalendarIcon = "carbon:calendar"
const AlertIcon = "mdi:alert-outline"
const AddIcon = "carbon:add"
const ChangelogIcon = "ion:sparkles-outline"

const props = defineProps<{
	maxItems?: number
}>()

const { t } = useStoreI18n()
const router = useRouter()
const storeNotification = useNotificationStore()
const changelogStore = useChangelogStore()

const displayItems = computed(() => {
	const items = storeNotification.getNotifications()
	if (props.maxItems) {
		return _take(items, props.maxItems)
	}
	return items
})

function onItemClick(item: Notification) {
	if (item.isChangelog) {
		storeNotification.showDrawer = false
		router.push({ name: "Changelog" })
		return
	}
	if (item.action) {
		preformAction(item.id, item.action)
		return
	}
	setRead(item.id)
}

function preformAction(id: string | number, action: () => void) {
	action()
	setRead(id)
}

async function setRead(id: string | number) {
	if (String(id).startsWith("changelog-")) {
		const changelogId = Number(String(id).replace("changelog-", ""))
		await changelogStore.markRead(changelogId)
		return
	}

	await axiosToken("post", `notifications/read/${id}/`)
	storeNotification.setNotification(id)
}
</script>

<style lang="scss" scoped>
.notifications-list {
	.item {
		position: relative;
		padding: 14px 0;

		.icon-box {
			width: 70px;
			min-width: 70px;
			display: flex;
			justify-content: center;
			position: relative;

			.n-icon {
				display: flex;
				justify-content: center;
				align-items: center;
				border-radius: 50%;
				width: 42px;
				height: 42px;
				margin-top: 2px;
			}

			.read-badge {
				position: absolute;
				top: 5px;
				left: 14px;
				width: 10px;
				height: 10px;
				border-radius: 50%;
				background-color: var(--primary-color);
				cursor: pointer;
			}
		}

		.content {
			padding-right: 20px;
			font-size: 14px;

			.title {
				font-weight: bold;

				:deep(h4),
				:deep(p) {
					margin: 0;
				}
			}

			.description {
				margin-top: 4px;
				font-size: 13px;
				opacity: 0.85;

				&--plain {
					display: -webkit-box;
					-webkit-line-clamp: 2;
					-webkit-box-orient: vertical;
					overflow: hidden;
					word-break: break-word;
				}
			}

			.footer {
				margin-top: 6px;

				.date {
					font-size: 12px;
					opacity: 0.5;
				}
				.action-text {
					font-size: 12px;
				}
			}
		}

		&.success {
			.icon-box {
				.n-icon {
					background-color: var(--primary-005-color);
					color: var(--success-color);
				}
			}
			.action-text {
				color: var(--primary-color);
			}
		}
		&.info {
			.icon-box {
				.n-icon {
					background-color: var(--secondary1-opacity-010-color);
					color: var(--info-color);
				}
			}
			.action-text {
				color: var(--info-color);
			}
		}
		&.warning {
			.icon-box {
				.n-icon {
					background-color: var(--secondary3-opacity-010-color);
					color: var(--warning-color);
				}
			}
			.action-text {
				color: var(--warning-color);
			}
		}
		&.error {
			.icon-box {
				.n-icon {
					background-color: var(--secondary4-opacity-010-color);
					color: var(--error-color);
				}
			}
			.action-text {
				color: var(--error-color);
			}
		}

		&.item--changelog {
			.icon-box .n-icon {
				background-color: var(--primary-005-color);
				color: var(--primary-color);
			}
		}

		&.pointer {
			cursor: pointer;
		}

		&:not(:last-child) {
			border-bottom: var(--border-small-050);
		}

		&:hover {
			background-color: var(--hover-005-color);
		}
	}
}
</style>
